import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, ArrowUpDown, FileText, Globe2, Receipt, Search } from 'lucide-react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Tab, TabList, TabPanel, Tabs, type Key } from 'react-aria-components';
import type { DocumentTab, SalesListQuery, SalesSort } from '../../api/endpoints';
import { salesListQuery } from '../../api/queries';
import type { SalesDocumentSummary } from '../../api/types';
import { useAccess } from '../../app/access';
import { useI18n } from '../../i18n/I18nProvider';
import { fillTemplate } from '../../lib/fillTemplate';
import { Button } from '../../ui/Button';
import { CommandBar, commandBarStyles } from '../../ui/CommandBar';
import { cx } from '../../ui/cx';
import { EmptyState } from '../../ui/Feedback';
import { Money, Page, useDocumentTitle } from '../../ui/Layout';
import { QueryError } from '../../ui/QueryError';
import { DataTable, Pagination, type Column } from '../../ui/Table';
import { useDocumentActions } from './actions';
import { SelectedDocumentZone } from './DocumentTools';
import { FindPanel } from './list/FindPanel';
import { FACET_KEYS, facetCount, tabSearch } from './list/views';
import styles from './Sales.module.css';
import { DocStatusBadge, LIVE_STATES, MockLabel, TaxStatusBadge, taxAxisOf } from './status';

export interface InvoiceListSearch {
  tab?: DocumentTab;
  q?: string;
  page?: number;
  limit?: number;
  sort?: SalesSort;
  dir?: 'asc' | 'desc';
  status?: string;
  taxStatus?: string;
  from?: string;
  to?: string;
  customer?: string;
  customerName?: string;
  minAmount?: string;
  maxAmount?: string;
}

const PAGE_SIZES = [25, 50, 100, 250, 500, 1000];
const FAMILIES = ['invoices', 'credit', 'debit'] as const;

export function parseListSearch(search: Record<string, unknown>): InvoiceListSearch {
  // The router's parser turns numeric-looking values into numbers (`minAmount=1000` → 1000);
  // every text parameter is read back as text.
  const str = (k: string) => {
    const v = search[k];
    if (typeof v === 'string') return v || undefined;
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    return undefined;
  };
  const num = (k: string) => {
    const n = Number(search[k]);
    return Number.isInteger(n) && n > 0 ? n : undefined;
  };
  const tab = str('tab');
  const sort = str('sort');
  const dir = str('dir');
  const out: InvoiceListSearch = {};
  if (tab === 'invoices' || tab === 'credit' || tab === 'debit') out.tab = tab;
  if (sort && ['number', 'date', 'customer', 'amount', 'status', 'updated'].includes(sort))
    out.sort = sort as SalesSort;
  if (dir === 'asc' || dir === 'desc') out.dir = dir;
  for (const k of [
    'q',
    'status',
    'taxStatus',
    'from',
    'to',
    'customer',
    'customerName',
    'minAmount',
    'maxAmount',
  ] as const) {
    const v = str(k);
    if (v) out[k] = v;
  }
  const page = num('page');
  const limit = num('limit');
  if (page) out.page = page;
  if (limit && PAGE_SIZES.includes(limit)) out.limit = limit;
  return out;
}

/**
 * MF-INV-001, billing documents (BLOCK-1-REDESIGN §A.1): family tabs and the last-updated line in
 * the header; a find panel (search, preset views, facets applied on change) beside a command bar
 * (start buttons, the selected document's tools) and the table, whose rows carry no menus.
 */
export function InvoiceListPage() {
  const { t } = useI18n();
  const { orgId = '' } = useParams({ strict: false });
  const search = useSearch({ strict: false }) as InvoiceListSearch;
  const navigate = useNavigate();
  const actions = useDocumentActions(orgId);
  useDocumentTitle(t.sales.title);

  const tab: DocumentTab = search.tab ?? 'invoices';
  const setSearch = (patch: Partial<InvoiceListSearch>, resetPage = true) =>
    void navigate({
      to: '/orgs/$orgId/sales/invoices',
      params: { orgId },
      search: (prev: InvoiceListSearch) => {
        const merged: Record<string, unknown> = { ...prev, ...patch };
        if (resetPage && !('page' in patch)) merged.page = undefined;
        return Object.fromEntries(
          Object.entries(merged).filter(([, v]) => v !== undefined && v !== ''),
        ) as InvoiceListSearch;
      },
      replace: true,
    });

  return (
    <Page>
      <Tabs
        selectedKey={tab}
        // D-6: a family is a different document register, so changing tab RESETS the find panel —
        // the search, facets, sort, direction, page and page size do not carry across. Only the tab
        // is kept (`tabSearch`), so the credit tab never opens on an invoices filter that hides its
        // own rows and reads as broken.
        onSelectionChange={(key: Key) =>
          void navigate({
            to: '/orgs/$orgId/sales/invoices',
            params: { orgId },
            search: tabSearch(key as DocumentTab),
            replace: true,
          })
        }
        className={styles.register}
      >
        <section className={styles.hero}>
          <div className={styles.heroRow}>
            <div className={styles.heroText}>
              <span className={styles.heroEyebrow}>
                <Receipt size={14} aria-hidden="true" />
                {t.sales.eyebrow}
              </span>
              <h1 className={styles.heroTitle}>{t.sales.title}</h1>
            </div>
            <TabList aria-label={t.sales.tabsLabel} className={styles.familyTabs}>
              {FAMILIES.map((key) => (
                <Tab key={key} id={key} className={styles.familyTab} data-testid={`tab-${key}`}>
                  {t.sales.tabs[key]}
                </Tab>
              ))}
            </TabList>
          </div>
          {tab === 'invoices' ? <LatestLine orgId={orgId} /> : null}
        </section>
        <TabPanel id="invoices" className={styles.familyPanel}>
          <InvoicesRegister
            orgId={orgId}
            tab="invoices"
            search={search}
            setSearch={setSearch}
            actions={actions}
          />
        </TabPanel>
        <TabPanel id="credit" className={styles.familyPanel}>
          <InvoicesRegister
            orgId={orgId}
            tab="credit"
            search={search}
            setSearch={setSearch}
            actions={actions}
          />
        </TabPanel>
        <TabPanel id="debit" className={styles.familyPanel}>
          <InvoicesRegister
            orgId={orgId}
            tab="debit"
            search={search}
            setSearch={setSearch}
            actions={actions}
          />
        </TabPanel>
      </Tabs>
      {actions.elements}
    </Page>
  );
}

/** «آخر ما تم تحديثه: INV-… · buyer · total · ETA status», one link under the title (DV-23). */
function LatestLine({ orgId }: { orgId: string }) {
  const { t } = useI18n();
  const latest = useQuery({
    ...salesListQuery(orgId, { tab: 'invoices', sort: 'updated', dir: 'desc', limit: 1 }),
    refetchInterval: (q) => {
      const r = q.state.data?.items[0];
      return r?.etaStatus && LIVE_STATES.has(r.etaStatus) ? 1500 : false;
    },
  });
  const row = latest.data?.items[0];
  if (!latest.data) return <p className={styles.latestLine} aria-hidden="true" />;
  if (!row) return <p className={styles.latestLine}>{t.sales.latestEmpty}</p>;
  const axis = taxAxisOf(row);
  return (
    <p className={styles.latestLine}>
      <Link
        to="/orgs/$orgId/sales/invoices/$docId"
        params={{ orgId, docId: row.id }}
        className={styles.latestLink}
        data-testid="latest-card"
      >
        <span>
          {fillTemplate(
            t.sales.latestLine,
            <span className={styles.mono} data-testid="latest-number">
              {row.number}
            </span>,
          )}
        </span>
        <span aria-hidden="true">·</span>
        <span>{row.cash ? t.sales.cash : (row.receiverName ?? t.sales.noCustomer)}</span>
        <span aria-hidden="true">·</span>
        <Money value={row.totalAmount} currency="EGP" />
        <span aria-hidden="true">·</span>
        {axis ? <TaxStatusBadge value={axis} /> : <DocStatusBadge value={row.status} />}
      </Link>
    </p>
  );
}

function InvoicesRegister({
  orgId,
  tab,
  search,
  setSearch,
  actions,
}: {
  orgId: string;
  /** The document family this register lists: invoices, or (Block 11) the credit or debit notes. */
  tab: DocumentTab;
  search: InvoiceListSearch;
  setSearch: (patch: Partial<InvoiceListSearch>, resetPage?: boolean) => void;
  actions: ReturnType<typeof useDocumentActions>;
}) {
  const { t, formatDate, formatDateTime } = useI18n();
  const navigate = useNavigate();
  const access = useAccess(orgId);
  // The credit and debit tabs are note registers: they carry the reference column and a note start
  // action, and no batch link (D-18, D-21, sales-notes.md §6). The invoices tab is unchanged.
  const isNoteTab = tab === 'credit' || tab === 'debit';

  const page = search.page ?? 1;
  const limit = search.limit ?? 25;
  const sort: SalesSort = search.sort ?? 'updated';
  const dir = search.dir ?? 'desc';
  const query: SalesListQuery = {
    tab,
    page,
    limit,
    sort,
    dir,
    ...(search.q ? { q: search.q } : {}),
    ...(search.status ? { status: search.status } : {}),
    ...(search.taxStatus ? { taxStatus: search.taxStatus } : {}),
    ...(search.from ? { from: search.from } : {}),
    ...(search.to ? { to: search.to } : {}),
    ...(search.customer ? { receiverPartyId: search.customer } : {}),
    ...(search.minAmount ? { minAmount: search.minAmount } : {}),
    ...(search.maxAmount ? { maxAmount: search.maxAmount } : {}),
  };
  const list = useQuery({
    ...salesListQuery(orgId, query),
    refetchInterval: (q) =>
      q.state.data?.items.some((r) => r.etaStatus && LIVE_STATES.has(r.etaStatus)) ? 1500 : false,
  });
  const rows = list.data?.items ?? [];

  // One selected document. It is kept by ID across polling and cleared when the tab, page, sort,
  // page size, search or facets change, or when the row is no longer on the page. The `tab` is part
  // of the key (WP-11.4 Slice 8): without it a selection made on the invoices tab would be read as
  // still selected on the credit tab (same id space, different family), a cross-tab selection leak.
  const scope = JSON.stringify([
    tab,
    page,
    limit,
    sort,
    dir,
    search.q,
    ...FACET_KEYS.map((k) => search[k]),
  ]);
  const [picked, setPicked] = useState<{ scope: string; id: string } | null>(null);
  const selected =
    picked && picked.scope === scope ? (rows.find((r) => r.id === picked.id) ?? null) : null;

  const open = (row: SalesDocumentSummary) =>
    void navigate({
      to: '/orgs/$orgId/sales/invoices/$docId',
      params: { orgId, docId: row.id },
    });
  const openRef = useRef(open);
  useLayoutEffect(() => {
    openRef.current = open;
  });

  const columns = useMemo<Column<SalesDocumentSummary>[]>(() => {
    const sortHeader = (key: SalesSort, label: string) => {
      const active = sort === key;
      const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
      return (
        <button
          type="button"
          className={styles.sortButton}
          aria-label={t.sales.sortBy(label)}
          data-testid={`sort-${key}`}
          data-sort={active ? dir : undefined}
          onClick={() =>
            setSearch({
              sort: key,
              dir: active && dir === 'asc' ? 'desc' : active ? 'asc' : 'desc',
            })
          }
        >
          {label}
          <Icon size={13} aria-hidden="true" />
        </button>
      );
    };
    const sorted = (key: SalesSort) =>
      sort === key ? (dir === 'asc' ? ('ascending' as const) : ('descending' as const)) : undefined;
    return [
      {
        key: 'tax',
        header: t.sales.columns.taxStatus,
        phoneOrder: 2,
        cell: (r) => {
          const axis = taxAxisOf(r);
          return (
            <>
              <span className={styles.statusCell}>
                {axis ? <TaxStatusBadge value={axis} testId="row-tax-status" /> : '—'}
                {r.status !== 'draft' && r.status !== 'discarded' ? (
                  <MockLabel environment={r.etaEnvironment} />
                ) : null}
              </span>
              {/* Block 11 (T11-03): a sibling line of plain text, no icon, no countdown. */}
              {r.declineWindow ? (
                <span className={styles.declineLine} data-testid="row-decline-window">
                  {r.declineWindow.until
                    ? t.sales.declineUntil(formatDateTime(r.declineWindow.until))
                    : t.sales.declineRunning}
                </span>
              ) : null}
              {/* Block 11 (RB3R11-11): a live cancellation on a valid/submitted row, same sibling
                  treatment, so a request needing a release is visible from the list. */}
              {r.cancellation ? (
                <span className={styles.declineLine} data-testid="row-cancellation">
                  {r.cancellation.state === 'requested'
                    ? t.sales.cancellationRequested
                    : r.cancellation.state === 'unconfirmed'
                      ? t.sales.cancellationUnconfirmed
                      : r.cancellation.effectiveAt
                        ? t.sales.cancellationPending(formatDateTime(r.cancellation.effectiveAt))
                        : t.sales.cancellationPendingNoDate}
                </span>
              ) : null}
            </>
          );
        },
      },
      {
        key: 'status',
        header: sortHeader('status', t.sales.columns.status),
        label: t.sales.columns.status,
        sort: sorted('status'),
        phoneOrder: 3,
        cell: (r) => <DocStatusBadge value={r.status} testId="row-status" />,
      },
      {
        key: 'number',
        header: sortHeader('number', t.sales.columns.number),
        label: t.sales.columns.number,
        sort: sorted('number'),
        isPrimary: true,
        phoneOrder: 1,
        // A plain anchor (BLOCK-1-REDESIGN §E performance): a router Link per row would build its
        // location on every render of 1,000 rows. A plain click navigates in the app; a modified
        // click (new tab or window) keeps the browser's behaviour.
        cell: (r) => (
          <a
            href={`/orgs/${encodeURIComponent(orgId)}/sales/invoices/${encodeURIComponent(r.id)}`}
            className={styles.numberLink}
            data-testid="row-number"
            title={r.status === 'discarded' ? t.sales.voidNote : undefined}
            onClick={(event) => {
              if (
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
              )
                return;
              event.preventDefault();
              openRef.current(r);
            }}
          >
            {r.number ?? '—'}
          </a>
        ),
      },
      // Block 11 (WP-11.4 Slice 8, sales-notes.md §6): the reference column — the note tabs only,
      // never the invoices tab. AC-T7: the first referenced number, then «+n» as PLAIN TEXT with an
      // accessible name, never a trigger, popover or menu.
      ...(isNoteTab
        ? [
            {
              key: 'reference',
              header: t.sales.columns.reference,
              label: t.sales.columns.reference,
              phoneOrder: 8,
              cell: (r) => {
                const ref = r.reference;
                if (!ref || ref.count === 0) return '—';
                const more = ref.count - 1;
                return (
                  <span data-testid="row-reference">
                    <span className={styles.mono}>{ref.first ?? '—'}</span>
                    {more > 0 ? (
                      <span
                        className={styles.subtle}
                        data-testid="row-reference-more"
                        aria-label={t.sales.referenceMore(more)}
                      >
                        {' +'}
                        {more}
                      </span>
                    ) : null}
                  </span>
                );
              },
            } as Column<SalesDocumentSummary>,
          ]
        : []),
      {
        key: 'customer',
        header: sortHeader('customer', t.sales.columns.customer),
        label: t.sales.columns.customer,
        sort: sorted('customer'),
        phoneOrder: 4,
        cell: (r) => (
          <span className={styles.customerCell}>
            <span data-testid="row-customer">
              {r.cash
                ? t.sales.cash
                : (r.receiverName ?? <span className={styles.subtle}>{t.sales.noCustomer}</span>)}
            </span>
            {r.receiverId ? (
              <span className={`${styles.subtle} ${styles.mono}`}>{r.receiverId}</span>
            ) : null}
          </span>
        ),
      },
      {
        key: 'amount',
        header: sortHeader('amount', t.sales.columns.amount),
        label: t.sales.columns.amount,
        sort: sorted('amount'),
        align: 'end',
        phoneOrder: 5,
        cell: (r) => (
          <span className={styles.amountCell}>
            <Money value={r.totalAmount} currency="EGP" testId="row-amount" />
            {r.currency !== 'EGP' && r.amountForeign ? (
              <span className={styles.subtle} data-testid="row-amount-foreign">
                <Money value={r.amountForeign} currency={r.currency} />
              </span>
            ) : null}
          </span>
        ),
      },
      {
        key: 'date',
        header: sortHeader('date', t.sales.columns.date),
        label: t.sales.columns.date,
        sort: sorted('date'),
        phoneOrder: 6,
        cell: (r) => (r.date ? formatDate(r.date) : '—'),
      },
      {
        key: 'updated',
        header: sortHeader('updated', t.sales.columns.updated),
        label: t.sales.columns.updated,
        sort: sorted('updated'),
        phoneOrder: 7,
        cell: (r) => <span className={styles.updatedCell}>{formatDateTime(r.lastActivityAt)}</span>,
      },
    ];
    // setSearch is recreated on every render but only reads the latest URL through navigate().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, orgId, isNoteTab, sort, dir, formatDate, formatDateTime]);

  const filterCount = facetCount(search);
  const hasCriteria = filterCount > 0 || Boolean(search.q);
  const isEmptyOrg = Boolean(list.data) && list.data?.total === 0 && !hasCriteria;
  const live = rows.some((r) => r.etaStatus && LIVE_STATES.has(r.etaStatus));
  const canCreate = access.can('invoice.create');
  const startInvoice = (kind: 'i' | 'ei') =>
    void navigate({
      to: '/orgs/$orgId/sales/invoices/new',
      params: { orgId },
      search: kind === 'ei' ? { kind: 'ei' } : {},
    });
  // Block 11 (sales-notes.md §6): starting a note opens the editor on the note kind — `c` on the
  // credit tab, `d` on the debit tab — where the reference is chosen. D-22 allows the sales role to
  // DRAFT a credit note; only issuing it needs the cancellation permission (enforced in the editor
  // and the server). So the start action rides on `invoice.create`, as the invoice start does.
  const noteKind = tab === 'credit' ? 'c' : 'd';
  const startNote = () =>
    void navigate({
      to: '/orgs/$orgId/sales/invoices/new',
      params: { orgId },
      search: { kind: noteKind },
    });
  const noteStartButton = (variant: 'primary' | 'secondary', testId: string) => (
    <Button
      variant={variant}
      icon={<FileText size={16} aria-hidden="true" />}
      testId={testId}
      onPress={startNote}
    >
      {tab === 'credit' ? t.sales.startCredit : t.sales.startDebit}
    </Button>
  );

  const commandBar = (
    <CommandBar
      label={selected ? t.sales.rowActions(selected.number ?? '') : t.sales.columns.actions}
      testId="command-bar"
      startLabel={t.sales.newMenu}
      start={
        canCreate ? (
          isNoteTab ? (
            noteStartButton('primary', 'new-note')
          ) : (
            <>
              <Button
                variant="primary"
                icon={<FileText size={16} aria-hidden="true" />}
                testId="new-invoice"
                onPress={() => startInvoice('i')}
              >
                {t.sales.newInvoice}
              </Button>
              <Button
                variant="secondary"
                icon={<Globe2 size={16} aria-hidden="true" />}
                testId="new-export-invoice"
                onPress={() => startInvoice('ei')}
              >
                {t.sales.newExportInvoice}
              </Button>
            </>
          )
        ) : undefined
      }
      dockSelection={Boolean(selected)}
      selection={
        selected ? (
          <SelectedDocumentZone
            orgId={orgId}
            doc={selected}
            actions={actions}
            onClear={() => setPicked(null)}
          />
        ) : (
          <span className={cx(styles.selectPrompt, commandBarStyles.selectionTools)}>
            {t.sales.selectPrompt}
          </span>
        )
      }
      end={
        live ? (
          <span className={styles.live} aria-live="polite">
            <span className={styles.dot} />
            {t.sales.updating}
          </span>
        ) : undefined
      }
    />
  );

  return (
    <div className={isEmptyOrg ? styles.registerBodyOnly : styles.registerBody}>
      {isEmptyOrg ? null : <FindPanel orgId={orgId} search={search} onChange={setSearch} />}
      <div className={styles.registerMain}>
        {commandBar}
        <p className="visually-hidden" role="status" aria-live="polite">
          {selected ? t.sales.selectAnnounce(selected.number ?? '') : ''}
        </p>
        {list.isError ? (
          <QueryError error={list.error} onRetry={() => void list.refetch()} />
        ) : (
          <DataTable
            caption={t.sales.title}
            testId="documents-table"
            columns={columns}
            rows={rows}
            getRowKey={(r) => r.id}
            getRowTestId={(r) => `row-${r.number ?? r.id}`}
            getRowClassName={(r) => (r.status === 'discarded' ? styles.void : undefined)}
            onRowClick={open}
            isLoading={list.isLoading}
            selection={{
              name: 'documents-select',
              selectedKey: selected?.id ?? null,
              onSelect: (r) => setPicked({ scope, id: r.id }),
              label: (r) => t.sales.selectRow(r.number ?? ''),
              header: t.sales.columns.select,
              testId: 'row-select',
            }}
            empty={
              hasCriteria ? (
                <EmptyState
                  icon={<Search size={28} />}
                  title={t.sales.noResultsTitle}
                  action={
                    <Button
                      variant="secondary"
                      onPress={() =>
                        setSearch({
                          q: undefined,
                          status: undefined,
                          taxStatus: undefined,
                          from: undefined,
                          to: undefined,
                          customer: undefined,
                          customerName: undefined,
                          minAmount: undefined,
                          maxAmount: undefined,
                        })
                      }
                    >
                      {t.sales.clearFilter}
                    </Button>
                  }
                >
                  {t.sales.noResultsBody}
                </EmptyState>
              ) : isNoteTab ? (
                // Block 11 (sales-notes.md §6): the note empty state names what a note needs and
                // carries the tab's own start action.
                <EmptyState
                  icon={<FileText size={28} />}
                  title={t.sales.noteEmpty}
                  action={canCreate ? noteStartButton('secondary', 'note-empty-start') : undefined}
                />
              ) : (
                <EmptyState icon={<Receipt size={28} />} title={t.sales.emptyTitle}>
                  {t.sales.emptyBody}
                </EmptyState>
              )
            }
            footer={
              list.data && list.data.total > 0 ? (
                <Pagination
                  page={page}
                  limit={limit}
                  total={list.data.total}
                  pageSizes={PAGE_SIZES}
                  showEnds
                  onPageChange={(p) => setSearch({ page: p }, false)}
                  onLimitChange={(l) => setSearch({ limit: l })}
                />
              ) : null
            }
          />
        )}
      </div>
    </div>
  );
}
